import { AxiosAPIClient, Headers, Request, Method } from '@surya-digital/tedwig';
import { AssetType, GetPresignedUploadURLRPC, GetPresignedUploadURLRPCClientImpl, VerifyFileUploadRPC, VerifyFileUploadRPCClientImpl, GetPresignedDownloadURLRPC, GetPresignedDownloadURLRPCClientImpl } from '@pulse/pulse-rpcs';
export { AssetType } from '@pulse/pulse-rpcs';

class FileServiceError extends Error {
    constructor(message) {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = "FileServiceError";
    }
}

class DeveloperError extends Error {
    constructor(message) {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = "DeveloperError";
    }
}

class FileServiceUnsupportedFileError extends FileServiceError {
    fileExtension;
    constructor(message, fileExtension) {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = "FileServiceUnsupportedFileError";
        this.fileExtension = fileExtension;
    }
}

class FileServiceMinSizeError extends FileServiceError {
    minSize;
    fileSize;
    constructor(message, minSize, fileSize) {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = "FileServiceMinSizeError";
        this.minSize = minSize;
        this.fileSize = fileSize;
    }
}

class FileServiceFileNameError extends FileServiceError {
    constructor(message) {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = "FileServiceFileNameError";
    }
}

class FileServiceMaxSizeError extends FileServiceError {
    maxSize;
    fileSize;
    constructor(message, maxSize, fileSize) {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = "FileServiceMaxSizeError";
        this.maxSize = maxSize;
        this.fileSize = fileSize;
    }
}

class FileServiceUploadError extends FileServiceError {
    error;
    constructor(message, error) {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = "FileServiceUploadError";
        this.error = error;
    }
}

class OperationUnsupportedError extends FileServiceError {
    constructor(message) {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = "OperationUnsupportedError";
    }
}

class FileServiceInvalidFileIdError extends FileServiceError {
    constructor(message) {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = "FileServiceInvalidFileIdErrorError";
    }
}

const assetObjects = {
    [AssetType.AssetType.RICH_TEXT_DOCUMENTS]: {
        allowedFileTypes: [
            {
                name: "csv",
                fileSize: {
                    minSize: 10,
                    maxSize: 1000000,
                },
            },
            {
                name: "xlsx",
                fileSize: {
                    minSize: 100,
                    maxSize: 500000,
                },
            },
            {
                name: "pdf",
                fileSize: {
                    minSize: 1,
                    maxSize: 1000000,
                },
            },
            {
                name: "jpg",
                fileSize: {
                    minSize: 1,
                    maxSize: 10000000,
                },
            },
            {
                name: "jpeg",
                fileSize: {
                    minSize: 1,
                    maxSize: 10000000,
                },
            },
            {
                name: "png",
                fileSize: {
                    minSize: 1,
                    maxSize: 10000000,
                },
            },
            {
                name: "svg",
                fileSize: {
                    minSize: 1,
                    maxSize: 10000000,
                },
            },
            {
                name: "gif",
                fileSize: {
                    minSize: 1,
                    maxSize: 10000000,
                },
            },
            {
                name: "webp",
                fileSize: {
                    minSize: 1,
                    maxSize: 10000000,
                },
            },
        ],
    },
};

class FileService {
    client;
    baseURL;
    /**
     * Constructs a new instance of the FileService class.
     * @param client - The API client for making requests to the file service.
     * @param baseURL - The base URL of the file service.
     */
    constructor(client, baseURL) {
        this.client = client;
        this.baseURL = baseURL;
    }
    getPresignedUploadURLAndFileId = async (file, assetType) => {
        try {
            const request = new GetPresignedUploadURLRPC.Request(file.name, file.size, assetType);
            const getPresignedUploadUrlRPC = new GetPresignedUploadURLRPCClientImpl(this.client);
            const { response, error } = await getPresignedUploadUrlRPC.execute(request);
            if (response) {
                return Promise.resolve({ presignedUploadUrl: response.presignedUploadURL, fileId: response.fileId });
            }
            else {
                switch (error?.code) {
                    case GetPresignedUploadURLError.InvalidFileType: {
                        return Promise.reject(new FileServiceUnsupportedFileError("This file type is not supported."));
                    }
                    case GetPresignedUploadURLError.UploadOperationUnsupported: {
                        return Promise.reject(new OperationUnsupportedError(`AssetType "${assetType}" does not support upload operation.`));
                    }
                    case GetPresignedUploadURLError.InvalidFileSize: {
                        // RPC is applying validation directly on the file-size provided explicitly.
                        // Hence this is a developer error as there shouldn't be any discrepancy between the file size validation of FileService and RPC.
                        return Promise.reject(new DeveloperError("File size validation failed."));
                    }
                    default: {
                        return Promise.reject(new DeveloperError("Unknown error."));
                    }
                }
            }
        }
        catch (error) {
            if (error instanceof GetPresignedUploadURLRPC.InvalidRequestError) {
                return Promise.reject(new DeveloperError(`Upload request validation failed: ${error.message}`));
            }
            else {
                // This part of the code is not reachable. For sanity, rejecting the promise with the error as is.
                return Promise.reject(error);
            }
        }
    };
    uploadFile = async (presignedUploadURL, file) => {
        return new Promise((resolve, reject) => {
            const uploadAxiosClient = new AxiosAPIClient({ timeoutMS: 0 });
            const uploadHeaders = new Headers([
                {
                    name: "Content-Type",
                    value: "multipart/form-data",
                }
            ]);
            uploadAxiosClient.sendRequest(new Request(Method.Put, presignedUploadURL.href, uploadHeaders, undefined, file))
                .then(() => resolve())
                .catch((error) => {
                reject(new FileServiceUploadError("File upload failed.", error));
            });
        });
    };
    verifyUpload = async (fileId) => {
        try {
            const request = new VerifyFileUploadRPC.Request(fileId);
            const verifyFileUploadRPC = new VerifyFileUploadRPCClientImpl(this.client);
            const { response, error } = await verifyFileUploadRPC.execute(request);
            if (response) {
                return;
            }
            else {
                switch (error?.code) {
                    case VerifyFileUploadError.FileIdNotFound:
                    case VerifyFileUploadError.VerificationFailed: {
                        return Promise.reject(new DeveloperError("Internal error. Uploaded file couldn't be verified."));
                    }
                    default: {
                        return Promise.reject(new DeveloperError("Internal error."));
                    }
                }
            }
        }
        catch (error) {
            if (error instanceof VerifyFileUploadRPC.InvalidRequestError) {
                return Promise.reject(new DeveloperError("Internal error."));
            }
            else {
                // This part of the code is not reachable. For sanity, rejecting the promise with the error as is.
                return Promise.reject(error);
            }
        }
    };
    validateFileSize = (file, fileType) => {
        const { maxSize, minSize } = fileType.fileSize;
        if (file.size > maxSize) {
            throw new FileServiceMaxSizeError(`File size is too large. Maximum allowed size is ${maxSize} bytes.`, maxSize, file.size);
        }
        if (file.size < minSize) {
            throw new FileServiceMinSizeError(`File size is too small. Minimum allowed size is ${minSize} bytes.`, minSize, file.size);
        }
    };
    validateAndGetFileTypeFromFile = (file, assetObject) => {
        const fileExtension = file.name.split(".").pop();
        const fileType = assetObject.allowedFileTypes.find(fileType => fileType.name === fileExtension);
        if (!fileType) {
            throw new FileServiceUnsupportedFileError(`File type "${fileExtension}" is not allowed.`, fileExtension);
        }
        return fileType;
    };
    validateFileName = (fileName) => {
        const maxFileNameLength = 500;
        const minFileNameLength = 2;
        const fileNameLength = fileName.length;
        if (fileNameLength !== fileName.trim().length) {
            throw new FileServiceFileNameError(`File name: "${fileName}" has leading and trailing spaces.`);
        }
        if (fileNameLength < 2) {
            throw new FileServiceFileNameError(`File name is too short. Length is ${fileNameLength}. Minimum allowed length is ${minFileNameLength}.`);
        }
        if (fileName.length > maxFileNameLength) {
            throw new FileServiceFileNameError(`File name is too long. Length is ${fileNameLength}. Maximum allowed length is ${maxFileNameLength}.`);
        }
        if (fileName.trim().length === 0) {
            throw new FileServiceFileNameError("File name cannot be empty.");
        }
    };
    validateUploadRequest = (file, assetType) => {
        this.validateFileName(file.name);
        const fileType = this.validateAndGetFileTypeFromFile(file, assetObjects[assetType]);
        this.validateFileSize(file, fileType);
    };
    /**
     * Uploads a file to the file service.
     *
     * @param file - The file to be uploaded.
     * @param assetType - The type of the asset associated with the file.
     *
     * @returns A promise that resolves to the file ID of the uploaded file.
     *
     * @throws FileServiceUnsupportedFileError - If the file type is not supported.
     * @throws FileServiceMinSizeError - If the file size is too small.
     * @throws FileServiceMaxSizeError - If the file size is too large.
     * @throws FileServiceFileNameError - If the file name is invalid.
     * @throws OperationUnsupportedError - If the specified asset-type doesn't support upload operation, or if the file type is not supported for this AssetType.
     * @throws FileServiceUploadError - If the file upload fails.
     * @throws DeveloperError - If an internal error occurs.
     */
    upload = async (file, assetType) => {
        try {
            this.validateUploadRequest(file, assetType);
            // Promise chaining is not possible here because `getPresignedUploadURLAndFileId` returns a fileId that is not returned by verifyUpload but is needed to resolve this promise finally.
            const { presignedUploadUrl, fileId } = await this.getPresignedUploadURLAndFileId(file, assetType);
            await this.uploadFile(presignedUploadUrl, file);
            await this.verifyUpload(fileId);
            return Promise.resolve(fileId);
        }
        catch (error) {
            return Promise.reject(error);
        }
    };
    /**
     * Retrieves a presigned download URL for a file associated with the given file ID.
     *
     * @param fileId - The ID of the file for which to retrieve the presigned download URL.
     *
     * @returns A promise that resolves to the presigned download URL.
     *
     * @throws FileServiceInvalidFileIdError - If the file ID is not found.
     * @throws OperationUnsupportedError - If the asset type associated with the file ID does not support download operation.
     * @throws DeveloperError - If an internal error occurs.
     */
    getPresignedDownloadURL = async (fileId) => {
        try {
            const request = new GetPresignedDownloadURLRPC.Request(fileId);
            const getPresignedDownloadURLRPC = new GetPresignedDownloadURLRPCClientImpl(this.client);
            const { response, error } = await getPresignedDownloadURLRPC.execute(request);
            if (response) {
                return Promise.resolve(response.presignedDownloadURL);
            }
            else {
                switch (error?.code) {
                    case GetPresignedDownloadURLError.FileIdNotFound: {
                        return Promise.reject(new FileServiceInvalidFileIdError(`File Id "${fileId}" not found.`));
                    }
                    case GetPresignedDownloadURLError.DownloadOperationUnsupported: {
                        return Promise.reject(new OperationUnsupportedError(`The AssetType associated with "${fileId}" does not support download operation.`));
                    }
                    default: {
                        return Promise.reject(new DeveloperError("Internal error."));
                    }
                }
            }
        }
        catch (error) {
            if (error instanceof GetPresignedDownloadURLRPC.InvalidRequestError) {
                return Promise.reject(new DeveloperError("Download request vallidation failed."));
            }
            else {
                // This part of the code is not reachable. For sanity, rejecting the promise with the error as is.
                return Promise.reject(error);
            }
        }
    };
    /**
     * Downloads a file from the provided URL.
     *
     * @param url - The URL from which to download the file.
     */
    downloadFromURL = (url) => {
        const anchorElement = document.createElement("a");
        anchorElement.href = url.href;
        anchorElement.download = "";
        document.body.appendChild(anchorElement);
        anchorElement.click();
        document.body.removeChild(anchorElement);
    };
    /**
     * Downloads a file from the file service using the provided file ID.
     *
     * @param fileId - The ID of the file to be downloaded.
     *
     * @returns A promise that resolves when the file is downloaded successfully.
     *
     * @throws FileServiceInvalidFileIdError - If the file ID is not found.
     * @throws OperationUnsupportedError - If the asset type associated with the file ID does not support download operation.
     * @throws DeveloperError - If an internal error occurs.
     *
     * @remarks
     * This method first retrieves the presigned download URL for the given file ID using the `getPresignedDownloadURL` method.
     * Then, it uses the `downloadFromURL` method to download the file from the obtained URL.
     *
     * @example
     * ```typescript
     * const fileService = new FileService(client, baseURL);
     * const fileId = new LeoUUID("123e4567-e89b-12d3-a456-426614174000");
     * try {
     *   await fileService.downloadFromFileId(fileId);
     *   console.log("File downloaded successfully.");
     * } catch (error) {
     *   console.error("Failed to download file:", error.message);
     * }
     * ```
     */
    downloadFromFileId = async (fileId) => {
        try {
            const downloadURL = await this.getPresignedDownloadURL(fileId);
            this.downloadFromURL(downloadURL);
        }
        catch (error) {
            return Promise.reject(error);
        }
    };
    /**
     * Builds a static URL for a file associated with the given file ID.
     *
     * @param baseURL - The base URL of the file service.
     * @param fileId - The ID of the file for which to build the static URL.
     *
     * @returns A URL object representing the static URL for the file.
     *
     * @remarks
     * This function is used to construct a static URL for a file associated with the given file ID. This URL doesn't expire.
     *
     * @example
     * ```typescript
     * const baseURL = "https://example.com";
     * const fileId = new LeoUUID("123e4567-e89b-12d3-a456-426614174000");
     * const staticURL = getStaticURL(fileId);
     * console.log(staticURL.href); // Output: "https://example.com/file-service/GetHttpPresignedDownloadURL?fileId=123e4567-e89b-12d3-a456-426614174000"
     * ```
     */
    getStaticURL = (fileId) => {
        return buildStaticURL(this.baseURL, fileId);
    };
}
// This function is outside of the FileService class, as it might be moved out of the scope of this file later.
const buildStaticURL = (baseURL, fileId) => {
    const staticURL = new URL("file-service/GetHttpPresignedDownloadURL", baseURL);
    staticURL.searchParams.set("fileId", fileId.uuid);
    return staticURL;
};
var GetPresignedUploadURLError;
(function (GetPresignedUploadURLError) {
    GetPresignedUploadURLError["InvalidFileType"] = "INVALID_FILE_TYPE";
    GetPresignedUploadURLError["InvalidFileSize"] = "INVALID_FILE_SIZE";
    GetPresignedUploadURLError["UploadOperationUnsupported"] = "UPLOAD_OPERATION_UNSUPPORTED";
})(GetPresignedUploadURLError || (GetPresignedUploadURLError = {}));
var VerifyFileUploadError;
(function (VerifyFileUploadError) {
    VerifyFileUploadError["FileIdNotFound"] = "FILE_ID_NOT_FOUND";
    VerifyFileUploadError["VerificationFailed"] = "VERIFICATION_FAILED";
})(VerifyFileUploadError || (VerifyFileUploadError = {}));
var GetPresignedDownloadURLError;
(function (GetPresignedDownloadURLError) {
    GetPresignedDownloadURLError["FileIdNotFound"] = "FILE_ID_NOT_FOUND";
    GetPresignedDownloadURLError["DownloadOperationUnsupported"] = "DOWNLOAD_OPERATION_UNSUPPORTED";
})(GetPresignedDownloadURLError || (GetPresignedDownloadURLError = {}));

export { DeveloperError, FileService, FileServiceError, FileServiceFileNameError, FileServiceInvalidFileIdError, FileServiceMaxSizeError, FileServiceMinSizeError, FileServiceUnsupportedFileError, FileServiceUploadError, OperationUnsupportedError };
//# sourceMappingURL=index.js.map
